import { AddOutlined, Search } from "@mui/icons-material";
import {
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useFormik } from "formik";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentMembers } from "src/store/reducers/organizations/organizationSlice";
import {
  Member,
  useLazyGetMembersQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import OrgAddUserModal from "./OrgAddUserModal/OrgAddUserModal";
import OrgMemberColumns from "./OrgMemberColumns/OrgMemberColumns";
import useStyles from "./OrgMembersList.styles";
import OrgTransferModal from "./OrgTransferModal/OrgTransferModal";
import OrgUpdateRolesModal from "./OrgUpdateRolesModal/OrgUpdateRolesModal";

interface OrgMemberListProps {
  orgId?: string;
}

const OrgMemberList = ({ orgId }: OrgMemberListProps) => {
  const classes = useStyles();

  const [member, setMember] = useState<Member>();

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  const [updateRolesModalOpen, setUpdateRolesModalOpen] = useState(false);

  const [transferUserModalOpen, setTransferUserModalOpen] = useState(false);

  const [memberGridRows, setMemberGridRows] = useState<Member[]>([]);

  const members = useAppSelector(selectCurrentMembers);

  const [getMembers, { isLoading }] = useLazyGetMembersQuery();

  useEffect(() => {
    setMemberGridRows(members);
  }, [members]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (orgId) {
        const { members } = await getMembers({
          organizationId: orgId || "",
        }).unwrap();

        setMemberGridRows(
          members
            .filter((m) => m.membershipStatus === "approved")
            .map((member) => ({ ...member })),
        );
      }
    };

    fetchMembers();
  }, [getMembers, orgId]);

  const formik = useFormik({
    initialValues: {
      search: "",
    },
    onSubmit: async (values) => {
      console.log("values", values);
    },
  });

  const { values, setFieldValue } = formik;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((searchValue) => {
      if (orgId) {
        getMembers({
          organizationId: orgId,
          search: searchValue,
        })
          .unwrap()
          .then((response) => {
            setMemberGridRows(response.members);
          });
      }
    }, 500),
    [],
  );

  const fetchMembers = async () => {
    if (orgId) {
      const { members } = await getMembers({
        organizationId: orgId || "",
      }).unwrap();

      setMemberGridRows(
        members
          .filter((m) => m.membershipStatus === "approved")
          .map((member) => ({ ...member })),
      );
    }
  };

  const openUpdateRolesModal = (member: Member) => {
    setMember(member);
    setUpdateRolesModalOpen(true);
  };

  const openTransferUserModal = (member: Member) => {
    setMember(member);
    setTransferUserModalOpen(true);
  };

  return isLoading ? (
    <Spinner
      animation="border"
      style={{ left: "50%", top: "50%", position: "relative" }}
    />
  ) : (
    <Grid item container className={classes.container}>
      <Grid item container justifyContent="space-between">
        <Typography variant="h4">User Access Management</Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setAddUserModalOpen(true)}
        >
          <AddOutlined />
          Invite User
        </Button>
      </Grid>
      <Grid item container className={classes.filtersContainer}>
        <TextField
          label="Search"
          name="search"
          type="text"
          value={values.search}
          onChange={(e) => {
            setFieldValue("search", e.target.value);
            debounceSearch(e.target.value);
          }}
          InputProps={{
            className: "text-field",
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          placeholder="Search by user"
        />
      </Grid>

      <Grid item container>
        <Paper className={classes.paperContainer}>
          <DataGrid
            rows={memberGridRows}
            hideFooterSelectedRowCount={true}
            headerHeight={50}
            pageSize={15}
            sx={{
              border: "none",
            }}
            rowHeight={108}
            className={classes.root}
            autoHeight
            columns={OrgMemberColumns(
              setMemberGridRows,
              openUpdateRolesModal,
              openTransferUserModal,
            )}
            getRowId={(row) => row.memberId}
          />
        </Paper>
      </Grid>

      <OrgAddUserModal
        orgId={orgId ?? "team-readyfit"}
        onClose={() => setAddUserModalOpen(false)}
        open={addUserModalOpen}
      />

      <OrgUpdateRolesModal
        orgId={orgId ?? "team-readyfit"}
        member={member}
        fetchMembers={fetchMembers}
        onClose={() => setUpdateRolesModalOpen(false)}
        open={updateRolesModalOpen}
      />

      <OrgTransferModal
        member={member}
        fetchMembers={fetchMembers}
        onClose={() => setTransferUserModalOpen(false)}
        open={transferUserModalOpen}
      />
    </Grid>
  );
};

export default OrgMemberList;
